/**
 * Footnote Popover Enhancement
 *
 * Enhances Gutenberg footnotes on desktop by showing the footnote content
 * in a popover when hovering over the footnote reference.
 *
 * Uses the Popover API with "hint" type and CSS Anchor Positioning for
 * a clean, simple implementation.
 */

import '../css/footnote.css';
import ready from 'document-ready';

let isNavigatingFromBacklink = false;
let currentViewportMode = null;

/**
 * Check if the Popover API is supported
 * @returns {boolean}
 */
function isPopoverSupported() {
  return typeof HTMLElement !== 'undefined' && 'popover' in HTMLElement.prototype;
}

/**
 * Check if the current viewport is mobile
 * @returns {boolean}
 */
function isMobileViewport() {
  return window.matchMedia('(max-width: 768px)').matches;
}

/**
 * Get the navbar height from CSS variable
 * @returns {number} Navbar height in pixels
 */
function getNavbarHeight() {
  const navbarHeight = getComputedStyle(document.documentElement)
    .getPropertyValue('--navbar-height')
    .trim();

  // Convert rem to pixels (assuming 1rem = 16px)
  if (navbarHeight.endsWith('rem')) {
    return parseFloat(navbarHeight) * 16;
  }

  return parseFloat(navbarHeight) || 76; // Default to 76px (4.75rem)
}

/**
 * Check if footnote reference would cause popover to overlay the navbar
 * @param {HTMLElement} footnoteRef - The footnote reference link
 * @returns {boolean}
 */
function wouldOverlayNavbar(footnoteRef) {
  const rect = footnoteRef.getBoundingClientRect();
  const navbarHeight = getNavbarHeight();

  // Estimate popover height (approximate, could be adjusted)
  const estimatedPopoverHeight = 100;

  // Check if popover positioned above would overlap with navbar
  // Add some buffer (20px) for the gap and arrow
  return rect.top - estimatedPopoverHeight - 20 < navbarHeight;
}

/**
 * Get the footnote content for a given reference
 * @param {HTMLElement} footnoteRef - The footnote reference link
 * @returns {string|null}
 */
function getFootnoteContent(footnoteRef) {
  const href = footnoteRef.getAttribute('href');

  if (!href || !href.startsWith('#')) {
    return null;
  }

  const targetId = href.substring(1);
  const targetElement = document.getElementById(targetId);

  if (!targetElement) {
    return null;
  }

  const contentClone = targetElement.cloneNode(true);
  const backlink = contentClone.querySelector('.footnote-backlink, a[href^="#"]');

  if (backlink) {
    backlink.remove();
  }

  return contentClone.innerHTML || contentClone.textContent;
}

/**
 * Setup popover for a single footnote reference
 * @param {HTMLElement} footnoteRef - The footnote reference link
 */
function setupFootnotePopover(footnoteRef) {
  const content = getFootnoteContent(footnoteRef);

  if (!content) {
    return;
  }

  const body = document.getElementsByTagName('body')[0];
  const isMobile = isMobileViewport();
  const popover = document.createElement('div');
  const popoverId = `footnote-popover-${footnoteRef.getAttribute('href').substring(1)}`;
  const shouldHaveWhiteBackground = !!footnoteRef.closest('.has-light-grey-background-color');

  popover.id = popoverId;
  popover.setAttribute('popover', isMobile ? 'manual' : 'hint');
  popover.className = isMobile ? 'footnote-popover footnote-popover--mobile' : 'footnote-popover';
  popover.innerHTML = content;

  if (shouldHaveWhiteBackground) {
    popover.classList.add('has-white-background');
  }

  if (body && body.classList.contains('is-using-pattern-blocks')) {
    footnoteRef.addEventListener('click', event => event.preventDefault());
  }

  if (isMobile) {
    const closeButton = document.createElement('button');

    closeButton.className = 'footnote-popover__close';
    closeButton.setAttribute('aria-label', 'Close');
    closeButton.innerHTML = '';

    closeButton.addEventListener('click', () => {
      popover.hidePopover();
    });

    popover.insertBefore(closeButton, popover.firstChild);

    document.body.appendChild(popover);

    footnoteRef.addEventListener('click', (e) => {
      // Don't open popover if we just navigated from a backlink
      if (isNavigatingFromBacklink) {
        return;
      }

      e.preventDefault();

      // Check if popover is still connected to DOM before showing
      if (popover.isConnected) {
        popover.showPopover();
      }
    });
  } else {
    const anchorName = `--footnote-anchor-${popoverId}`;

    footnoteRef.style.anchorName = anchorName;
    popover.style.positionAnchor = anchorName;

    if (wouldOverlayNavbar(footnoteRef)) {
      popover.classList.add('footnote-popover--below');
    }

    document.body.appendChild(popover);

    let hideTimeout = null;

    const showPopoverHandler = () => {
      // Don't open popover if we just navigated from a backlink
      if (isNavigatingFromBacklink) {
        return;
      }

      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }

      // Check if popover is still connected to DOM before showing
      if (!popover.isConnected) {
        return;
      }

      if (wouldOverlayNavbar(footnoteRef)) {
        popover.classList.add('footnote-popover--below');
      } else {
        popover.classList.remove('footnote-popover--below');
      }

      popover.showPopover();
    };

    const hidePopoverHandler = () => {
      hideTimeout = setTimeout(() => {
        popover.hidePopover();
      }, 200);
    };

    footnoteRef.addEventListener('mouseover', showPopoverHandler);
    footnoteRef.addEventListener('mouseout', hidePopoverHandler);
    footnoteRef.addEventListener('click', event => {
      event.preventDefault();
      showPopoverHandler();
    });

    popover.addEventListener('mouseover', showPopoverHandler);
    popover.addEventListener('mouseout', hidePopoverHandler);

    footnoteRef.addEventListener('focus', () => {
      // Don't open popover if we just navigated from a backlink
      if (isNavigatingFromBacklink) {
        return;
      }

      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }

      // Check if popover is still connected to DOM before showing
      if (popover.isConnected) {
        popover.showPopover();
      }
    });

    footnoteRef.addEventListener('blur', () => {
      hideTimeout = setTimeout(() => {
        popover.hidePopover();
      }, 200);
    });
  }
}

/**
 * Modify footnotes block to move backlinks to beginning and replace arrow with caret
 */
function modifyFootnotesBlock() {
  const footnoteItems = document.querySelectorAll('ol.wp-block-footnotes li');

  footnoteItems.forEach(item => {
    const backlink = item.querySelector('.footnote-backlink, a[href^="#"][aria-label*="Fußnotenreferenz"], a[href^="#"][aria-label*="footnote reference"]');

    if (backlink) {
      const space = document.createTextNode(' ');

      backlink.textContent = '^';
      item.insertBefore(backlink, item.firstChild);
      item.insertBefore(space, backlink.nextSibling);
    }
  });
}

/**
 * Setup backlink listeners to prevent popover opening when navigating back
 */
function setupBacklinkListeners() {
  const backlinks = document.querySelectorAll('ol.wp-block-footnotes li a[href^="#"]');

  backlinks.forEach(backlink => {
    backlink.addEventListener('click', () => {
      isNavigatingFromBacklink = true;

      setTimeout(() => {
        isNavigatingFromBacklink = false;
      }, 500);
    });
  });
}

/**
 * Cleanup all existing footnote popovers
 */
function cleanupFootnotePopovers() {
  const popovers = document.querySelectorAll('.footnote-popover');
  const footnoteRefs = document.querySelectorAll('sup.fn a[href^="#"]');

  popovers.forEach(popover => {
    popover.remove();
  });

  footnoteRefs.forEach(ref => {
    ref.style.anchorName = '';
  });
}

/**
 * Initialize footnote popovers
 */
function initFootnotePopovers() {
  if (!isPopoverSupported()) {
    return;
  }

  const footnoteRefs = document.querySelectorAll('sup.fn a[href^="#"]');

  currentViewportMode = isMobileViewport() ? 'mobile' : 'desktop';
  modifyFootnotesBlock();
  footnoteRefs.forEach(setupFootnotePopover);
  setupBacklinkListeners();
}

/**
 * Handle viewport changes (resize and orientation change)
 */
let resizeTimeout = null;

function handleViewportChange() {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }

  resizeTimeout = setTimeout(() => {
    const newViewportMode = isMobileViewport() ? 'mobile' : 'desktop';

    if (newViewportMode !== currentViewportMode) {
      cleanupFootnotePopovers();
      initFootnotePopovers();
    }
  }, 150);
}

ready(initFootnotePopovers);

window.addEventListener('resize', handleViewportChange);
window.addEventListener('orientationchange', handleViewportChange);

